import { upsertUserPreference } from 'App/services/UserPreferenceService';

/**
 * Permanent, per-user marker written to localStorage once a user's legacy
 * preferences have been migrated. Because it is NOT prefixed with `${userId}_`,
 * it will never be picked up by {@link getLegacyUserPreferenceEntries}.
 */
const LEGACY_PREFERENCE_MIGRATION_SENTINEL_PREFIX = 'stm:preferencesMigrated:';

/**
 * Short-lived cookie used to throttle the migration check so we don't re-scan
 * localStorage on every page load. See {@link LEGACY_PREFERENCE_MIGRATION_TTL_DAYS}.
 */
const LEGACY_PREFERENCE_MIGRATION_COOKIE = 'stmPreferencesMigrationChecked';

/**
 * How long (in days) to skip re-checking after an attempt.
 */
const LEGACY_PREFERENCE_MIGRATION_TTL_DAYS = 1;

/**
 * Legacy values were written by `SessionStorageService.setOnUser`, which
 * `JSON.stringify`s objects but stores primitives/strings as-is. Reverse that:
 * try to parse JSON, otherwise fall back to the raw string.
 *
 * @param {string|null} raw
 * @returns {*}
 */
function parseLegacyPreferenceValue(raw) {
    if (raw == null) {
        return raw;
    }
    try {
        return JSON.parse(raw);
    } catch (_e) {
        // Plain string value that was never JSON-encoded (e.g. "this_week").
        return raw;
    }
}

/**
 * Collects every legacy per-user preference stored in localStorage. These are
 * the keys `setOnUser`/`getOnUser` wrote, namespaced as `${userId}_${name}`.
 *
 * @param {string} userId
 * @returns {Array<{ storageKey: string, name: string, value: * }>}
 */
function getLegacyUserPreferenceEntries(userId) {
    if (!userId) {
        return [];
    }

    const prefix = `${userId}_`;

    return Object.keys(window.localStorage)
        .filter((storageKey) => storageKey.startsWith(prefix) && storageKey.length > prefix.length)
        .map((storageKey) => ({
            storageKey,
            name: storageKey.slice(prefix.length),
            value: parseLegacyPreferenceValue(window.localStorage.getItem(storageKey)),
        }));
}

/**
 * Migrates a user's legacy localStorage preferences to the backend preferences
 * API. The backend is the source of truth, so any key it already knows about is
 * skipped (never clobbered). Only keys the backend is missing are pushed.
 *
 * The legacy localStorage copies are intentionally left in place: the still-live
 * AngularJS controllers continue to read them, and keeping them makes the
 * migration idempotent/retryable (the per-key skip prevents duplicate writes on
 * subsequent runs once the backend has the value).
 *
 * @param {object} params
 * @param {string} params.userId
 * @param {Object<string, { name: string, value: * }>} [params.preferences] - The
 *   preferences already known to the backend, keyed by name.
 * @returns {Promise<Array<{ storageKey: string, name: string, value: * }>>} The
 *   entries that were pushed to the API (empty if there was nothing to migrate).
 */
async function migrateLegacyUserPreferences({ userId, preferences = {} }) {
    const entries = getLegacyUserPreferenceEntries(userId);

    if (entries.length === 0) {
        return [];
    }

    // Only push keys the backend does not already have — never clobber server data.
    const pending = entries.filter((entry) => !preferences[entry.name]);

    await Promise.all(pending.map(({ name, value }) => upsertUserPreference({ name, value })));

    return pending;
}

export {
    LEGACY_PREFERENCE_MIGRATION_COOKIE,
    LEGACY_PREFERENCE_MIGRATION_SENTINEL_PREFIX,
    LEGACY_PREFERENCE_MIGRATION_TTL_DAYS,
    getLegacyUserPreferenceEntries,
    migrateLegacyUserPreferences,
    parseLegacyPreferenceValue,
    upsertUserPreference,
};
